import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Sparkles, Check, Info } from 'lucide-react';
import { HelpTooltip } from '../components/HelpTooltip';
import api from '../utils/api';
import { toast } from 'sonner';

export default function AIAdvisor() {
  const [data, setData] = useState(null);
  const [applying, setApplying] = useState(null);

  const load = () => api.get('/advisor/recommendations').then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const apply = async (rec) => {
    setApplying(rec.rec_id);
    try {
      const { data: res } = await api.post(`/advisor/apply/${rec.rec_id}`);
      toast.success(res.result || 'Applied');
      load();
    } catch {
      toast.error("Couldn't apply — try again");
    } finally {
      setApplying(null);
    }
  };

  return (
    <Layout variant="light">
      <div className="max-w-3xl mx-auto" data-testid="advisor-page">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles size={26} className="text-lime" />
          <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-2">AI ROI Advisor
            <HelpTooltip text="ROI = Return On Investment — money made vs. money spent. This advisor reads your campaign numbers each week and suggests one-click fixes: pause what's wasting money, push what's booking jobs." testId="help-advisor-title" />
          </h1>
        </div>
        <p className="text-light-gray mb-8">Tap a suggestion → squeeze more jobs out of the same budget. Refreshed weekly.</p>

        {!data ? (
          <p className="text-gray">Loading your recommendations…</p>
        ) : (
          <div className="space-y-4">
            {data.recommendations.map((rec) => (
              <div key={rec.rec_id} className={`rounded-2xl border p-5 ${rec.status === 'applied' ? 'border-lime/40 bg-lime/5' : 'border-divider bg-card-bg'}`} data-testid={`advisor-rec-${rec.rec_id}`}>
                <div className="flex items-start justify-between gap-4 mb-2">
                  <p className="text-white font-bold text-lg">{rec.title}</p>
                  {rec.status === 'applied' ? (
                    <span className="shrink-0 inline-flex items-center gap-1 text-lime text-sm font-bold" data-testid="rec-applied-badge"><Check size={15} /> Applied</span>
                  ) : rec.action?.type !== 'none' && (
                    <button onClick={() => apply(rec)} disabled={applying === rec.rec_id} data-testid={`apply-btn-${rec.rec_id}`}
                      className="shrink-0 px-4 py-2 rounded-lg bg-lime text-black text-sm font-black hover:bg-lime/90 disabled:opacity-50">
                      {applying === rec.rec_id ? 'Applying…' : 'Apply'}
                    </button>
                  )}
                </div>
                <p className="text-sm text-light-gray mb-3">{rec.reason}</p>
                <p className="text-sm font-bold text-lime mb-3" data-testid="rec-impact">💰 {rec.est_impact}</p>
                <div className="flex gap-2 items-start rounded-lg bg-black border border-divider p-3">
                  <Info size={14} className="text-lime shrink-0 mt-0.5" />
                  <p className="text-xs text-gray">{rec.metric_note}</p>
                </div>
              </div>
            ))}
            <p className="text-xs text-gray mt-4">Generated {new Date(data.generated_at).toLocaleDateString()} · refreshes weekly from your campaign spend, cost per lead, and lead counts.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
